import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import LoginDropdown from "../../sections/header/LoginDropdown";
import LogoUploader from "../../sections/header/LogoUploader";

export default function EducaeetHero({ content, onContentChange, isSelected, defaultLogo }: SectionContentProps) {
    const primaryBg = content.colorPalette?.primary || "#074d3d"; // Deep forest emerald
    const accentYellow = content.colorPalette?.accent || "#ffbd14"; // Warm golden yellow
    const fontFamily = content.fontFamily || "'Inter', 'Plus Jakarta Sans', system-ui, sans-serif";

    const handleLogoChange = (newLogoUrl: string) => {
        if (onContentChange) {
            onContentChange({ ...content, logo: newLogoUrl });
        }
    };

    const stats = content.stats || [
        { value: "15k+", label: "Active Students" },
        { value: "10k+", label: "Online Video Courses" }
    ];

    return (
        <div
            className={`relative overflow-hidden w-full ${isSelected ? "ring-2 ring-blue-500" : ""}`}
            style={{ backgroundColor: primaryBg, fontFamily }}
        >
            {/* Inline Style for Floating, Rotation & Left-to-Right Highlight Draw Keyframes */}
            <style>{`
                @keyframes floatSlow {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-12px); }
                }
                @keyframes floatSlower {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-16px); }
                }
                @keyframes rotateStripe {
                    0% { transform: translate(-50%, -50%) rotate(0deg); }
                    100% { transform: translate(-50%, -50%) rotate(360deg); }
                }
                @keyframes highlightDraw {
                    0% { transform: scaleX(0); }
                    100% { transform: scaleX(1); }
                }
                .animate-float-1 {
                    animation: floatSlow 3.8s ease-in-out infinite;
                }
                .animate-float-2 {
                    animation: floatSlower 4.6s ease-in-out infinite 0.6s;
                }
                .animate-rotate-stripe {
                    animation: rotateStripe 35s linear infinite;
                }
                .animate-highlight-draw {
                    transform-origin: left center;
                    animation: highlightDraw 1.2s cubic-bezier(0.25, 1, 0.5, 1) forwards 0.3s;
                }
            `}</style>

            {/* ================= BACKGROUND DECORATIVE SVGS ================= */}

            {/* Top-Left Arch / Radial Dot Pattern */}
            <div className="absolute top-28 left-4 opacity-25 pointer-events-none z-0 hidden md:block">
                <svg width="220" height="220" viewBox="0 0 200 200" fill="none">
                    <g fill="#ffffff">
                        <circle cx="20" cy="20" r="3.5" /> <circle cx="45" cy="20" r="3.5" /> <circle cx="70" cy="20" r="3.5" /> <circle cx="95" cy="20" r="3.5" /> <circle cx="120" cy="20" r="3.5" />
                        <circle cx="20" cy="45" r="3.5" /> <circle cx="45" cy="45" r="3.5" /> <circle cx="70" cy="45" r="3.5" /> <circle cx="95" cy="45" r="3.5" /> <circle cx="120" cy="45" r="3.5" />
                        <circle cx="20" cy="70" r="3.5" /> <circle cx="45" cy="70" r="3.5" /> <circle cx="70" cy="70" r="3.5" /> <circle cx="95" cy="70" r="3.5" />
                        <circle cx="20" cy="95" r="3.5" /> <circle cx="45" cy="95" r="3.5" /> <circle cx="70" cy="95" r="3.5" />
                        <circle cx="20" cy="120" r="3.5" /> <circle cx="45" cy="120" r="3.5" />
                        <circle cx="20" cy="145" r="3.5" />
                    </g>
                </svg>
            </div>

            {/* Bottom-Left Matrix Grid Dots */}
            <div className="absolute bottom-6 left-8 opacity-20 pointer-events-none z-0 hidden md:block">
                <svg width="140" height="100" viewBox="0 0 140 100" fill="none">
                    <g fill="#ffffff">
                        {Array.from({ length: 24 }).map((_, i) => (
                            <circle
                                key={i}
                                cx={(i % 6) * 22 + 10}
                                cy={Math.floor(i / 6) * 22 + 10}
                                r="3.5"
                            />
                        ))}
                    </g>
                </svg>
            </div>

            {/* Top-Right Confetti / Organic Dashes */}
            <div className="absolute top-28 right-12 opacity-40 pointer-events-none z-0 hidden lg:block">
                <svg width="160" height="160" viewBox="0 0 160 160" fill="none" stroke="#ffffff" strokeWidth="4" strokeLinecap="round">
                    <line x1="20" y1="30" x2="32" y2="20" />
                    <line x1="50" y1="20" x2="58" y2="35" />
                    <line x1="80" y1="15" x2="95" y2="22" />
                    <line x1="120" y1="30" x2="110" y2="45" />
                    <line x1="140" y1="60" x2="150" y2="75" />
                    <line x1="30" y1="70" x2="45" y2="65" />
                    <line x1="65" y1="60" x2="75" y2="75" />
                    <line x1="100" y1="70" x2="115" y2="65" />
                    <line x1="130" y1="90" x2="142" y2="105" />
                    <line x1="40" y1="110" x2="52" y2="125" />
                    <line x1="80" y1="105" x2="95" y2="115" />
                    <line x1="110" y1="125" x2="125" y2="120" />
                </svg>
            </div>

            {/* ================= TRANSPARENT EMBEDDED SPLIT NAVBAR ================= */}
            <nav className="relative z-20 w-full">
                <div className="relative container mx-auto px-6 md:px-12 py-6 flex items-center">

                    {/* LEFT HALF — Nav items */}
                    <ul className="hidden md:flex items-center gap-8 flex-1">
                        {(content.navItems || ["Home", "Courses", "About", "Contact"]).slice(0, 2).map((item: any, idx: number) => {
                            const label = typeof item === "string" ? item : item.label;
                            const sectionId = typeof item === "string" ? item.toLowerCase() : item.sectionId;
                            return (
                                <li key={idx}>
                                    <a
                                        href={`#${sectionId}`}
                                        className="text-sm font-semibold uppercase tracking-wider transition-colors text-white/90 hover:text-white cursor-pointer"
                                    >
                                        <EditableText
                                            value={label}
                                            onChange={(v) => {
                                                const newNav = [...(content.navItems || ["Home", "Courses", "About", "Contact"])];
                                                newNav[idx] = typeof item === "string" ? v : { ...item, label: v };
                                                onContentChange?.({ ...content, navItems: newNav });
                                            }}
                                        />
                                    </a>
                                </li>
                            );
                        })}
                    </ul>

                    {/* CENTER — Logo absolutely centered */}
                    <div className="absolute left-0 right-0 flex justify-center pointer-events-none">
                        <div className="pointer-events-auto flex items-center gap-2.5">
                            <LogoUploader
                                currentLogo={content.logo}
                                defaultLogo={defaultLogo}
                                onLogoChange={handleLogoChange}
                                heightClass="h-10"
                                primaryColor={primaryBg}
                                secondaryColor={accentYellow}
                                companyName={content.companyName || "Instacks"}
                                isEditing={!!onContentChange}
                            />
                        </div>
                    </div>

                    {/* RIGHT HALF — Remaining nav items + Login */}
                    <div className="hidden md:flex items-center gap-8 flex-1 justify-end">
                        {(content.navItems || ["Home", "Courses", "About", "Contact"]).slice(2).map((item: any, idx: number) => {
                            const label = typeof item === "string" ? item : item.label;
                            const sectionId = typeof item === "string" ? item.toLowerCase() : item.sectionId;
                            const actualIdx = idx + 2;
                            return (
                                <a
                                    key={idx}
                                    href={`#${sectionId}`}
                                    className="text-sm font-semibold uppercase tracking-wider transition-colors text-white/90 hover:text-white cursor-pointer"
                                >
                                    <EditableText
                                        value={label}
                                        onChange={(v) => {
                                            const newNav = [...(content.navItems || ["Home", "Courses", "About", "Contact"])];
                                            newNav[actualIdx] = typeof item === "string" ? v : { ...item, label: v };
                                            onContentChange?.({ ...content, navItems: newNav });
                                        }}
                                    />
                                </a>
                            );
                        })}
                        <LoginDropdown
                            variant="button"
                            className="inline-flex items-center gap-2 px-5 py-2 rounded-full font-bold text-sm transition-all hover:scale-105 hover:shadow-lg active:scale-95 shadow-md cursor-pointer !text-gray-900"
                            style={{ background: accentYellow }}
                        />
                    </div>

                </div>
            </nav>

            {/* ================= MAIN HERO CONTAINER ================= */}
            <div className="container mx-auto px-6 md:px-12 py-10 md:py-16 relative z-10">
                <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-6">

                    {/* LEFT COLUMN - TEXT CONTENT */}
                    <div className="flex-1 max-w-xl text-left z-10">
                        <h1 className="text-4xl sm:text-5xl lg:text-[60px] font-extrabold text-white leading-[1.16] mb-6 tracking-tight">
                            <EditableText
                                value={content.title || "Discover, Learn, and Grow Smarter with Instacks"}
                                onChange={(v) => onContentChange?.({ ...content, title: v })}
                            />
                        </h1>


                        <div className="text-sm sm:text-base text-emerald-100/90 mb-10 leading-relaxed font-normal max-w-md">
                            <EditableText
                                value={content.subtitle || "Instacks offers expert-led courses, modern tools, and a supportive environment to help learners grow, achieve success, and build a brighter future."}
                                onChange={(v) => onContentChange?.({ ...content, subtitle: v })}
                                as="span"
                            />
                        </div>

                        {/* CTA Button */}
                        <div>
                            <LoginDropdown
                                variant="button"
                                className="inline-flex items-center gap-3 px-7 py-3.5 rounded-xl font-bold !text-gray-900 shadow-xl transition-all duration-200 hover:scale-105 hover:shadow-2xl active:scale-95 cursor-pointer text-sm sm:text-base"
                                style={{ background: accentYellow, borderRadius: "12px" }}
                            />
                        </div>
                    </div>

                    {/* RIGHT COLUMN - STUDENT IMAGE & DUAL BACKDROP CIRCLES & FLOATING CARDS */}
                    <div className="flex-1 relative w-full flex justify-center lg:justify-end items-center mt-6 lg:mt-0">
                        <div className="relative w-full max-w-[540px] flex justify-center items-center">

                            {/* CIRCLE 1: Outer Solid Light-Green Circle */}
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[360px] h-[360px] sm:w-[460px] sm:h-[460px] lg:w-[520px] lg:h-[520px] rounded-full bg-[#115847] -z-20 shadow-inner" />

                            {/* CIRCLE 2: Inner Rotating Circle with Yellow Stripes */}
                            <div className="animate-rotate-stripe absolute top-1/2 left-1/2 w-[300px] h-[300px] sm:w-[380px] sm:h-[380px] lg:w-[420px] lg:h-[420px] rounded-full overflow-hidden -z-10 flex items-center justify-center">
                                <div
                                    className="w-[150%] h-[150%] opacity-40 shrink-0"
                                    style={{
                                        backgroundImage: `repeating-linear-gradient(45deg, ${accentYellow} 0, ${accentYellow} 16px, transparent 16px, transparent 36px)`
                                    }}
                                />
                            </div>

                            {/* Main Hero Student Image Cutout */}
                            <div className="relative z-10 flex justify-center items-end min-h-[440px]">
                                {content.image ? (
                                    <img
                                        src={content.image}
                                        alt="Instacks Student"
                                        className="max-h-[500px] sm:max-h-[560px] w-auto object-contain drop-shadow-2xl"
                                    />
                                ) : (
                                    <img
                                        src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&q=80&w=800"
                                        alt="Instacks Student"
                                        className="max-h-[460px] sm:max-h-[520px] w-auto object-cover rounded-3xl shadow-2xl"
                                    />
                                )}
                            </div>

                            {/* FLOATING CARD 1 - Active Students (Bottom Center-Left with floatSlow animation) */}
                            <div 
                                onClick={(e) => e.stopPropagation()}
                                className="animate-float-1 absolute -bottom-2 left-2 sm:-left-6 z-30 bg-white rounded-2xl shadow-2xl p-4 sm:p-5 flex items-center gap-4 border border-gray-100/90 min-w-[210px] hover:ring-2 hover:ring-blue-400 transition-all group/float1 cursor-pointer"
                            >
                                <div
                                    className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 shadow-sm"
                                    style={{ backgroundColor: accentYellow }}
                                >
                                    {/* Student Mortarboard Icon */}
                                    <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0112 20.055a11.952 11.952 0 01-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                                    </svg>
                                </div>
                                <div className="flex-1">
                                    <div className="text-[12px] font-semibold text-gray-400 mb-0.5">
                                        <EditableText
                                            value={stats[0]?.label || "Active Students"}
                                            onChange={(v) => {
                                                const newStats = [...stats];
                                                newStats[0] = { ...newStats[0], label: v };
                                                onContentChange?.({ ...content, stats: newStats });
                                            }}
                                        />
                                    </div>
                                    <div className="text-2xl font-black text-gray-900 tracking-tight flex items-center">
                                        <EditableText
                                            value={stats[0]?.value || "15k+"}
                                            onChange={(v) => {
                                                const newStats = [...stats];
                                                newStats[0] = { ...newStats[0], value: v };
                                                onContentChange?.({ ...content, stats: newStats });
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* FLOATING CARD 2 - Online Video Courses (Middle-Right with floatSlower animation) */}
                            <div 
                                onClick={(e) => e.stopPropagation()}
                                className="animate-float-2 absolute top-1/2 -right-4 sm:-right-8 -translate-y-1/2 z-30 bg-white rounded-2xl shadow-2xl p-4 sm:p-5 flex items-center gap-4 border border-gray-100/90 min-w-[220px] hover:ring-2 hover:ring-blue-400 transition-all group/float2 cursor-pointer"
                            >
                                <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 bg-[#074d3d] shadow-sm">
                                     {/* Video Screen Icon */}
                                     <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                         <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                                         <line x1="8" y1="21" x2="16" y2="21" />
                                         <line x1="12" y1="17" x2="12" y2="21" />
                                         <polygon points="10 8 16 10 10 12 10 8" fill="currentColor" />
                                     </svg>
                                </div>
                                <div className="flex-1">
                                    <div className="text-[12px] font-semibold text-gray-400 mb-0.5">
                                        <EditableText
                                            value={stats[1]?.label || "Online Video Courses"}
                                            onChange={(v) => {
                                                const newStats = [...stats];
                                                newStats[1] = { ...newStats[1], label: v };
                                                onContentChange?.({ ...content, stats: newStats });
                                            }}
                                        />
                                    </div>
                                    <div className="text-2xl font-black text-gray-900 tracking-tight flex items-center">
                                        <EditableText
                                            value={stats[1]?.value || "10k+"}
                                            onChange={(v) => {
                                                const newStats = [...stats];
                                                newStats[1] = { ...newStats[1], value: v };
                                                onContentChange?.({ ...content, stats: newStats });
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}


